/**
 * The reference data and write-authority split a course form needs.
 *
 * Two surfaces now render the same fields — the section record's edit dialog on
 * Sections, and the Courses list's guided create flow (issue #485). The
 * instructor-eligibility predicate in particular must exist once: it decides
 * which faculty a section may name as its instructor of record, and #473 made
 * that the difference between a section that publishes and a 422 the registrar
 * cannot act on.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";

import type { CourseFormSources, InstructorOption } from "@/lib/course-form";
import { useTenantConfig } from "@/hooks/use-tenant-config";
import { academicStructureApi } from "@/lib/api/academicStructure";
import { facultyApi, type FacultyProfile } from "@/lib/api/faculty";
import { personChoice } from "@/lib/person-name";
import { placementRead } from "@/components/academic-structure/placement-read";

/**
 * The rule `_verify_assignable_faculty_profile` in `courses.py` applies (ADR
 * 0034). A profile whose person holds no membership here is assignable: the
 * registrar builds the teaching team first and invites the teacher to sign in
 * after. A profile whose person signs in must do so as an active administrator
 * or instructor. A membership always has a login, so no `user_id` is no
 * membership.
 */
export function instructorOption(f: FacultyProfile): InstructorOption {
  const option = { faculty_profile_id: f.id, label: personChoice(f.display_name, f.email), archived: Boolean(f.archived_at) };
  if (f.archived_at) return { ...option, eligible: false, qualifier: "Archived" };
  if (f.user_id === null) return { ...option, eligible: true, qualifier: "No sign-in yet" };
  const eligible = f.account_is_active === true
    && (f.account_role === "admin" || f.account_role === "instructor");
  return {
    ...option,
    eligible,
    qualifier: eligible ? null : f.account_is_active === false ? "Deactivated" : "Unavailable",
  };
}

export function useCourseFormSources({
  canWriteRegistrarFields,
  enabled = true,
}: {
  canWriteRegistrarFields: boolean;
  /** A learner never names an instructor, so their page never reads faculty. */
  enabled?: boolean;
}): CourseFormSources {
  const queryClient = useQueryClient();
  const { meeting, locations, memberStatus } = useTenantConfig();
  const programs = useQuery({ queryKey: ["programs"], queryFn: academicStructureApi.getPrograms });
  const semesters = useQuery({ queryKey: ["semesters"], queryFn: academicStructureApi.getSemesters });
  const programChoices = placementRead(programs), termChoices = placementRead(semesters);
  // Fetched for instructors too, not only admins. The instructor of record is
  // ADMIN-ONLY TO CHANGE, but the form still has to NAME the current one, and
  // an empty directory made the field read blank for a section that has an
  // instructor. Not a permission boundary either way: `GET /faculty` is
  // `verify_auth`.
  const faculty = useQuery({
    queryKey: ["faculty"],
    queryFn: () => facultyApi.list(),
    enabled,
  });

  return {
    // `meeting` is null both before the member read lands and after it fails,
    // so the read's own status is what answers this rather than the value.
    pending: memberStatus === "loading"
      || programs.isPending
      || semesters.isPending
      || (enabled && faculty.isPending),
    unavailable: [
      ...(programs.isError ? ["programs"] : []),
      ...(semesters.isError ? ["terms"] : []),
      ...(enabled && faculty.isError ? ["instructors"] : []),
      ...(memberStatus === "error" || memberStatus === "disabled" ? ["delivery options"] : []),
    ],
    retry: () => {
      void programs.refetch(); void semesters.refetch();
      if (enabled) void faculty.refetch();
      void queryClient.invalidateQueries({ queryKey: ["tenant", "me"] });
    },
    programs: programChoices.choices,
    semesters: termChoices.choices,
    placementReads: {
      program: programChoices.state,
      semester: termChoices.state,
    },
    instructors: (faculty.data ?? []).map(instructorOption),
    canWriteRegistrarFields,
    enabledProviders: meeting?.enabled_providers ?? ["in_person"],
    defaultMeetingProvider: meeting?.default_meeting_provider ?? "in_person",
    locations,
  };
}
